// Hook pour détecter la vitesse et la direction du scroll
import { useState, useEffect, useRef } from 'react';
import { LazyLoadLogger } from '../Services/LazyLoadLogger';

export type ScrollDirection = 'up' | 'down' | 'none';

export interface UseScrollSpeedReturn {
    scrollSpeed: number;           // Vitesse actuelle en px/s
    scrollDirection: ScrollDirection;
    isScrolling: boolean;
    scrollPosition: number;        // Position actuelle
    lastActiveSpeed: number;       // Dernière vitesse significative (ne descend pas à 0)
}

interface ScrollSample {
    position: number;
    timestamp: number;
}

const SIGNIFICANT_SPEED_THRESHOLD = 50; // px/s minimum pour être considéré comme actif
const SPEED_DECAY_TIME = 5000; // Garder la vitesse active pendant 5 secondes après l'arrêt

/**
 * Hook pour détecter la vitesse de scroll avec moyenne glissante
 * Maintient la dernière vitesse significative pour le préchargement
 */
export function useScrollSpeed(): UseScrollSpeedReturn {
    const [scrollSpeed, setScrollSpeed] = useState(0);
    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('none');
    const [isScrolling, setIsScrolling] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [lastActiveSpeed, setLastActiveSpeed] = useState(0);

    const samplesRef = useRef<ScrollSample[]>([]);
    const lastSpeedRef = useRef(0);
    const lastActiveSpeedRef = useRef(0);
    const lastActiveTimeRef = useRef(Date.now());
    const scrollTimeoutRef = useRef<number>();
    const decayTimeoutRef = useRef<number>();

    useEffect(() => {
        const handleScroll = () => {
            const currentPosition = window.scrollY;
            const currentTime = performance.now();

            setScrollPosition(currentPosition);
            setIsScrolling(true);

            // Ajouter un échantillon
            samplesRef.current.push({
                position: currentPosition,
                timestamp: currentTime
            });

            // Garder seulement les 10 derniers échantillons (moyenne glissante)
            if (samplesRef.current.length > 10) {
                samplesRef.current.shift();
            }

            // Calculer la vitesse moyenne
            if (samplesRef.current.length >= 2) {
                const firstSample = samplesRef.current[0];
                const lastSample = samplesRef.current[samplesRef.current.length - 1];

                const deltaPosition = lastSample.position - firstSample.position;
                const deltaTime = lastSample.timestamp - firstSample.timestamp;

                if (deltaTime > 0) {
                    // Vitesse en px/s
                    const speed = Math.abs(deltaPosition / deltaTime * 1000);
                    const direction: ScrollDirection = deltaPosition > 0 ? 'down' : deltaPosition < 0 ? 'up' : 'none';

                    setScrollSpeed(speed);
                    setScrollDirection(direction);

                    // Mettre à jour la dernière vitesse active si significative
                    if (speed >= SIGNIFICANT_SPEED_THRESHOLD) {
                        lastActiveSpeedRef.current = speed;
                        lastActiveTimeRef.current = Date.now();
                        setLastActiveSpeed(speed);
                        
                        // Annuler le decay timeout si l'utilisateur scroll à nouveau
                        if (decayTimeoutRef.current) {
                            clearTimeout(decayTimeoutRef.current);
                            decayTimeoutRef.current = undefined;
                        }
                    }

                    // Logger si changement significatif (> 20%)
                    const speedChange = Math.abs(speed - lastSpeedRef.current) / (lastSpeedRef.current || 1);
                    if (speedChange > 0.2) {
                        LazyLoadLogger.logViewport(`Vitesse de scroll: ${speed.toFixed(0)} px/s (${direction})`, {
                            speed: speed.toFixed(2),
                            direction,
                            position: currentPosition,
                            change: `${(speedChange * 100).toFixed(0)}%`,
                            lastActiveSpeed: lastActiveSpeedRef.current.toFixed(0)
                        });
                        lastSpeedRef.current = speed;
                    }
                }
            }

            // Reset après 150ms d'inactivité
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = window.setTimeout(() => {
                setIsScrolling(false);
                setScrollSpeed(0);
                setScrollDirection('none');
                samplesRef.current = [];
                LazyLoadLogger.logViewport('Scroll arrêté', {
                    maintienLastActiveSpeed: lastActiveSpeedRef.current.toFixed(0)
                });
                
                // Programmer le decay de lastActiveSpeed après SPEED_DECAY_TIME
                if (!decayTimeoutRef.current && lastActiveSpeedRef.current > 0) {
                    decayTimeoutRef.current = window.setTimeout(() => {
                        const timeSinceActive = Date.now() - lastActiveTimeRef.current;
                        if (timeSinceActive >= SPEED_DECAY_TIME) {
                            // Réduire progressivement vers 0
                            lastActiveSpeedRef.current = 0;
                            setLastActiveSpeed(0);
                            LazyLoadLogger.logViewport('Vitesse active expirée (5s d\'inactivité)');
                        }
                        decayTimeoutRef.current = undefined;
                    }, SPEED_DECAY_TIME);
                }
            }, 150);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            if (decayTimeoutRef.current) {
                clearTimeout(decayTimeoutRef.current);
            }
        };
    }, []);

    return {
        scrollSpeed,
        scrollDirection,
        isScrolling,
        scrollPosition,
        lastActiveSpeed
    };
}

